import * as React from 'react';

import { breakpoints } from '@notino/react-styleguide';

import { styled } from '@linaria/react';
import { assignRefs } from '@utils/assignRefs';

const Placeholder = styled.div`
  @media (min-width: ${breakpoints.lg}) {
    display: none;
  }
`;

interface IProps {
  tabsAbsoluteContainerEl: HTMLDivElement;
}

const createObserver = (placeholderEl: HTMLDivElement) =>
  new ResizeObserver((entries: ResizeObserverEntry[]) => {
    for (const entry of entries) {
      if (entry.borderBoxSize) {
        // Firefox implements `borderBoxSize` as a single content rect, rather than an array
        const borderBoxSize: ResizeObserverSize = Array.isArray(
          entry.borderBoxSize
        )
          ? entry.borderBoxSize[0]
          : entry.borderBoxSize;

        placeholderEl.style.height = `${borderBoxSize.blockSize}px`;
      }
    }
  });

const MobileTabContentPlaceholder = React.forwardRef<HTMLDivElement, IProps>(
  ({ tabsAbsoluteContainerEl }, ref) => {
    const placeholderRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
      if (!placeholderRef.current || !tabsAbsoluteContainerEl) {
        return;
      }

      const observer = createObserver(placeholderRef.current);
      observer.observe(tabsAbsoluteContainerEl);
      return () => observer.disconnect();
    }, [tabsAbsoluteContainerEl]);

    return (
      <Placeholder
        ref={assignRefs(
          placeholderRef,
          ref as React.MutableRefObject<HTMLDivElement | null>
        )}
      />
    );
  }
);

export const useMoveTabsToMobilePlaceholder = () => {
  const absoluteTabsWrapperRef = React.useRef<HTMLDivElement>(null);
  const tabsPlaceholderRef = React.useRef<HTMLDivElement>(null);
  const topSectionRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const absoluteEl = absoluteTabsWrapperRef.current;
    const placeholderEl = tabsPlaceholderRef.current;
    const topSectionEl = topSectionRef.current;

    if (!absoluteEl || !placeholderEl || !topSectionEl) {
      return;
    }

    const observer = new ResizeObserver(() => {
      absoluteEl.style.top = `${
        placeholderEl.offsetTop - topSectionEl.offsetTop
      }px`;
    });
    observer.observe(topSectionEl);

    return () => {
      observer.disconnect();
    };
  }, []);

  return { absoluteTabsWrapperRef, tabsPlaceholderRef, topSectionRef };
};

export default MobileTabContentPlaceholder;
